import React, { useCallback, useEffect, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { Box, Button, Grid } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { BillingAddress, PartialBy, RootState } from '../../redux/types';
import BillingAddressCard from './AddressCard';
import ApiClient from '../../loopback/apiClient';
import { showNotification } from '../../redux/actions';

type Props = PropsFromRedux & {
  onSelect?: (id: number) => void;
}

const BillingDashboard: React.FC<Props> = ({
  userId,
  onSelect,
  showNotification: showNotificationAction,
}: Props) => {
  const [billingAddressList, setBillingAddressList] = useState<BillingAddress[]>();
  const [editId, setEditId] = useState<number>();

  useEffect(() => {
    ApiClient.getBillingAddress()
      .then((adresses) => {
        setBillingAddressList(adresses);
      });
  }, [userId]);

  const addBillingAddress = useCallback((adr: BillingAddress) => {
    if (billingAddressList) {
      setBillingAddressList([...billingAddressList, adr]);
    } else {
      setBillingAddressList([adr]);
    }
  }, [billingAddressList]);

  const updateBillingAddress = useCallback((adr: BillingAddress) => {
    if (billingAddressList) {
      const newArr = billingAddressList.map((a) => {
        if (a.id === adr.id) {
          return adr;
        }
        return a;
      });
      setBillingAddressList(newArr);
    }
  }, [billingAddressList]);

  const deleteBillingAddress = useCallback((adr: BillingAddress) => {
    const newArr = billingAddressList?.filter((a) => a.id !== adr.id);
    setBillingAddressList(newArr);
  }, [billingAddressList]);

  const addInstituteAddressAsBillingAddress = async () => {
    if (!userId) {
      return;
    }
    const userWithInstitute = await ApiClient.getUserWithInstitute(userId);
    const { institute } = userWithInstitute;
    if (!institute) {
      showNotificationAction(<FormattedMessage id="billingAddress.noInstitute" />);
      return;
    }
    const billingData: PartialBy<BillingAddress, 'id'> = {
      name: `${institute.name}${institute.hospitalName ? ', institute.hospitalName' : ''}`,
      city: institute.city,
      country: institute.country,
      contactNumber: institute.phone,
      street: institute.street,
      houseNumber: institute.housenumber,
      postcode: Number(institute.zip),
      quipUserId: userId,
    };
    const billingAddress = await ApiClient.addBillingAddress(billingData);
    addBillingAddress(billingAddress);
  };

  if (!userId) {
    return null;
  }
  const addAddressButton = billingAddressList && billingAddressList.length >= 6
    ? []
    : [{ id: -1 } as BillingAddress];
  return (
    <Box>
      <Grid container spacing={2}>
        {
          billingAddressList && (
            addAddressButton.concat(billingAddressList)
              .map((ba) => (
                ba.id === -1
                  ? (
                    <Grid key={ba.id} item xs={12} sm={6} md={6} lg={4} xl={3}>
                      <BillingAddressCard
                        addBillingAddress={addBillingAddress}
                        updateBillingAddress={updateBillingAddress}
                        deleteBillingAddress={deleteBillingAddress}
                        userId={userId}
                        editMode={editId === ba.id}
                        setEditId={setEditId}
                        add
                      />
                    </Grid>
                  )
                  : (
                    <Grid key={ba.id} item xs={12} sm={6} md={6} lg={4} xl={3}>
                      <BillingAddressCard
                        billingAddress={ba}
                        addBillingAddress={addBillingAddress}
                        updateBillingAddress={updateBillingAddress}
                        deleteBillingAddress={deleteBillingAddress}
                        editMode={editId === ba.id}
                        setEditId={setEditId}
                        onSelect={onSelect}
                      />
                    </Grid>
                  )
              ))
          )
        }
      </Grid>
      <Box sx={{ mt: 2 }}>
        {
          billingAddressList && billingAddressList.length < 6 && (
            <Button variant="contained" color="primary" onClick={addInstituteAddressAsBillingAddress}>
              <FormattedMessage id="billingAddress.addInstitute" />
            </Button>
          )
        }
      </Box>
    </Box>
  );
};

const mapStateToProps = (state: RootState) => ({
  userId: state.users?.token?.userId,
});

const dispatchProps = {
  showNotification,
};

const connector = connect(mapStateToProps, dispatchProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export const BillingDashboardPure = BillingDashboard;
export default connector(BillingDashboard);
